import "./CreateVisit.css";
import React, { useContext, useEffect, useState } from "react";
import { DefaultButton, IComboBoxOption, ITextFieldStyles, TextField, Toggle } from "@fluentui/react";
import {
  GetVisitorsQueryVariables,
  InputVisit,
  InputVisiting,
  InputVisitor,
  useCreateVisitorMutation,
  useCreateVisitsForSelfMutation,
  useGetVisitorsLazyQuery,
} from "@graphql";
import Schedule, { ScheduleDataSource } from "../../shared/schedule/Schedule";
import BackHeader from "../../shared/back-header/BackHeader";
import VisitorDetailsIcon from "assets/svg/visitor-details-icon";
import HostIcon from "assets/svg/host-icon";
import EmailIcon from "assets/svg/email-icon";
import AddressBookIcon from "assets/svg/address-book-icon";
import { useHistory, useParams } from "react-router-dom";
import { UserContext } from "../../Auth";
import SuccessfulAction from "../../shared/successful-action/SuccessfulAction";
import CountryAndPhone from "../../shared/country-and-phone/CountryAndPhone";

interface CreateVisitSource {
  firstName?: string;
  lastName?: string;
  email?: string;
  countryCode?: string;
  phoneNumber?: string;
  vehicleRegistration?: string;
  company?: string;
  jobTitle?: string;
  scheduleItems: ScheduleDataSource[];
  hostId: string;
  emailInvitation: boolean;
  saveToAddressBook: boolean;
}

const CreateVisit: React.FunctionComponent = () => {
  const [currentIdLocation, setCurrentIdLocation] = useState<string>();
  const [getData, { data: visitorsData, loading }] = useGetVisitorsLazyQuery();
  const [comboBoxItems, setComboBoxItems] = useState<IComboBoxOption[]>([]);
  const [dateTimeItems, setDateTimeItems] = useState<ScheduleDataSource[]>([]);
  const routerHistory = useHistory();
  let { idLocationParam } = useParams<{ idLocationParam: string }>();
  const user = useContext(UserContext);
  const [firstNameStyle, setFirstNameStyle] = useState<Partial<ITextFieldStyles>>();
  const [lastNameStyle, setLastNameStyle] = useState<Partial<ITextFieldStyles>>();
  const [validationMessage, setValidationMessage] = useState<string>("");

  const [showSuccessSave, setShowSuccessSave] = useState<boolean>(false);

  const [visitor, setVisitor] = useState<CreateVisitSource>({ hostId: user._id } as CreateVisitSource);

  const [createVisits, { data: createVisitData, loading: createVisitLoading, error: createVisitError }] =
    useCreateVisitsForSelfMutation({
      onCompleted: async (data) => {
        const test = data;
        setShowSuccessSave(true);
      },
      onError: (error) => {
        setValidationMessage("Visit creation failed, please try again!");
        console.error(`Create Visit Error Occurred Error details: ${JSON.stringify(error)}`);
      },
    });

  const [createVisitor, { data: createVisitorData, loading: createVisitorLoading, error: createVisitorError }] =
    useCreateVisitorMutation({
      onCompleted: async (data) => {
        const newVisitorId = data?.createVisitor?._id;
        creatingVisits(null, newVisitorId);
      },
      onError: (error) => {
        setValidationMessage("Visit creation failed, please try again!");
        console.error(`Create Visitor Error Occurred! Error details: ${JSON.stringify(error)}`);
      },
    });

  useEffect(() => {
    setCurrentIdLocation(idLocationParam);
    setFirstNameStyle(textFieldStyles);
    setLastNameStyle(textFieldStyles);

    const variables: GetVisitorsQueryVariables = {
      locationId: currentIdLocation,
      hidePrivateVisitors: true,
    };
    getData({ variables: variables });
  }, []);

  useEffect(() => {
    visitorsData?.visitors?.map((el) => {
      comboBoxItems.push({ key: el._id, text: `${el.firstName} ${el.lastName}` });
    });
  }, [visitorsData]);

  const onScheduleItemsChanged = (items: ScheduleDataSource[]) => {
    setValidationMessage("");
    visitor.scheduleItems = items;
  };

  const textFieldStyles: Partial<ITextFieldStyles> = {
    fieldGroup: {
      backgroundColor: "#f2f3f6",
      height: "2.5rem",
      border: "0",
      borderRadius: "4px 4px 0 0",
      borderBottom: "1px solid #48454E",
      ":after": {
        border: 0,
        outline: "none",
      },
    },
  };

  const textFieldErrorStyles: Partial<ITextFieldStyles> = {
    fieldGroup: {
      backgroundColor: "#f2f3f6",
      height: "2.5rem",
      border: "0",
      color: "red",
      borderRadius: "4px 4px 0 0",
      borderBottom: "1px solid #ba1b1b",

      ":hover": {
        borderRadius: "4px 4px 0 0",
        borderBottom: "1px solid #ba1b1b",
      },
    },
  };

  const getUserInitials = (): string => {
    let initials = `${user.firstName?.charAt(0)?.toLocaleUpperCase()} 
      ${user.lastName?.charAt(0)?.toLocaleUpperCase()}`;

    return initials;
  };

  const validateForm = (): boolean => {
    let message = "";
    if (!visitor.firstName) {
      setFirstNameStyle(textFieldErrorStyles);
      message = "Visitor names cannot be empty!";
    }
    if (!visitor.lastName) {
      setLastNameStyle(textFieldErrorStyles);
      message = "Visitor names cannot be empty!";
    }
    if (!message) {
      if (!visitor.scheduleItems || visitor.scheduleItems.length < 1) {
        message = "Please, populate the schedule section!";
      } else {
        message = visitor.scheduleItems.filter((it) => it.dateHasError || it.startTimeHasError)?.[0]?.errorMessage;
      }
    }
    setValidationMessage(message);
    if (message) return false;
    return true;
  };

  const createVisit = () => {
    if (!validateForm()) return;

    setVisitor({ ...visitor });

    const newVisitor: InputVisitor = {
      firstName: visitor.firstName,
      lastName: visitor.lastName,
      email: visitor.email,
      countryCode: visitor.phoneNumber ? visitor.countryCode : "",
      mobile: visitor.phoneNumber,
      carRegistration: visitor.vehicleRegistration,
      company: visitor.company,
      jobTitle: visitor.jobTitle,
    };

    if (visitor.saveToAddressBook) {
      createVisitor({ variables: { visitor: newVisitor } });
    } else {
      creatingVisits(newVisitor, null);
    }
  };

  const creatingVisits = (newVisitor: InputVisitor, newVisitorId: string) => {
    const inputVisit: InputVisit[] = [] as InputVisit[];

    visitor.scheduleItems.map((el) => {
      const expectedDate: string = new Date(
        el.date.getFullYear(),
        el.date.getMonth(),
        el.date.getDate(),
        el.startTime.getHours(),
        el.startTime.getMinutes(),
        el.startTime.getSeconds(),
      ).toUTCString();

      let expectedEnd;
      if (el.endTime) {
        expectedEnd = new Date(
          el.date.getFullYear(),
          el.date.getMonth(),
          el.date.getDate(),
          el.endTime.getHours(),
          el.endTime.getMinutes(),
          el.endTime.getSeconds(),
        ).toUTCString();
      }

      const visit: InputVisit = {
        expectedDate: expectedDate,
        expectedEnd: expectedEnd,
        locationId: currentIdLocation,
        sendInvitation: visitor.emailInvitation,
        visitor: newVisitor,
        visitorId: newVisitorId,
        visiting: {
          id: user._id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
        } as InputVisiting,
      } as InputVisit;

      inputVisit.push(visit);
    });

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    createVisits({ variables: { visits: inputVisit, timezone: timezone } });
  };

  const refreshStates = () => {
    setShowSuccessSave(false);
    setValidationMessage("");
    setVisitor({} as CreateVisitSource);
    setLastNameStyle(textFieldStyles);
    setFirstNameStyle(textFieldStyles);
  };

  return (
    <>
      {showSuccessSave && (
        <>
          <SuccessfulAction
            headerTitle={"Create Visit"}
            title="Visit created"
            description={"Your visitor has been created, you can now view this visit in RICOH spaces."}
            actionButtonText={"Create another"}
            onCloseClick={() => {
              routerHistory.push(`/visitors/visitorsList/${currentIdLocation}`);
            }}
            onActionButtonClick={() => {
              refreshStates();
            }}
          />
        </>
      )}
      {!showSuccessSave && (
        <>
          <div className="create-visit-main-container">
            <BackHeader title={"Create Visit"} />
            <hr className="create-visit-dividing"></hr>
            <div>
              <VisitorDetailsIcon></VisitorDetailsIcon>
              <span className="details-header-text"> Visitor Details</span>
            </div>
            <div>
              <div>
                <div className="text-create-visit-left">
                  <TextField
                    placeholder="First name *"
                    styles={firstNameStyle}
                    onChange={(ev, newValue) => {
                      setValidationMessage("");
                      newValue?.length > 0
                        ? setFirstNameStyle(textFieldStyles)
                        : setFirstNameStyle(textFieldErrorStyles);
                      visitor.firstName = newValue;
                    }}
                  />
                </div>
                <div className="text-create-visit-right">
                  <TextField
                    placeholder="Last name *"
                    styles={lastNameStyle}
                    onChange={(ev, newValue) => {
                      setValidationMessage("");
                      newValue?.length > 0 ? setLastNameStyle(textFieldStyles) : setLastNameStyle(textFieldErrorStyles);
                      visitor.lastName = newValue;
                    }}
                  />
                </div>
              </div>
              <div className="text-create-visit-one-row">
                <TextField
                  placeholder="Email"
                  styles={textFieldStyles}
                  onChange={(ev, newValue) => {
                    visitor.email = newValue;
                  }}
                />
              </div>

              <div className="text-create-visit-country-code">
                <CountryAndPhone
                  onValueChanged={(countryCode, phone) => {
                    visitor.countryCode = countryCode;
                    visitor.phoneNumber = phone;
                  }}
                ></CountryAndPhone>
              </div>
              <div>
                <div className="text-create-visit-left">
                  <TextField
                    placeholder="Vehicle registration"
                    styles={textFieldStyles}
                    onChange={(ev, newValue) => {
                      visitor.vehicleRegistration = newValue;
                    }}
                  />
                </div>
                <div className="text-create-visit-right">
                  <TextField
                    placeholder="Company"
                    styles={textFieldStyles}
                    onChange={(ev, newValue) => {
                      visitor.company = newValue;
                    }}
                  />
                </div>
              </div>
              <div className="text-create-visit-one-row">
                <TextField
                  placeholder="Job title"
                  styles={textFieldStyles}
                  onChange={(ev, newValue) => {
                    visitor.jobTitle = newValue;
                  }}
                />
              </div>
            </div>
            <div className="create-visit-schedule-main-container">
              <Schedule items={dateTimeItems} onItemsChanged={onScheduleItemsChanged}></Schedule>
            </div>
            <hr className="create-visit-dividing"></hr>
            <div className="create-visit-host-main-container">
              <HostIcon></HostIcon>
              <span className="create-visit-host-header-text">Host</span>
              <div className="create-visit-host-date">
                {user.picture && (
                  <div className="create-visit-host-img">
                    <img src={user.picture}></img>
                  </div>
                )}
                {!user.picture && <div className="create-visit-host-initials">{getUserInitials()}</div>}

                <div className="create-visit-host-name-title">
                  <div className="host-person-name"> {`${user.firstName} ${user.lastName}`}</div>
                  <div className="host-job-title">{user.jobTitle}</div>
                </div>
              </div>
            </div>
            <hr className="create-visit-dividing"></hr>
            <div className="create-visit-email-address-main-container">
              <div className="create-visit-email-container">
                <div className="create-visit-email-address-icon">
                  <EmailIcon></EmailIcon>
                </div>
                <div className="create-visit-email-address-text">Email invitation</div>
                <div className="create-visit-email-address-toggle">
                  <Toggle
                    onChange={(ev, checked) => {
                      visitor.emailInvitation = checked;
                    }}
                    styles={{ root: { marginBottom: 0 } }}
                  />
                </div>
                <span className="create-visit-email-text"></span>
              </div>

              <div className="create-visit-address-container">
                <div className="create-visit-email-address-icon">
                  <AddressBookIcon></AddressBookIcon>
                </div>
                <div className="create-visit-email-address-text">Save to address book</div>
                <div className="create-visit-email-address-toggle">
                  <Toggle
                    onChange={(ev, checked) => {
                      visitor.saveToAddressBook = checked;
                    }}
                    styles={{
                      root: { marginBottom: 0 },
                      pill: {
                        // background: "red",
                      },
                    }}
                  />
                </div>
                <span className="create-visit-email-text"></span>
              </div>
            </div>
          </div>

          <div className="create-visit-bottom-panel">
            <hr className="create-visit-dividing create-visit-dividing-bottom"></hr>
            <div className="create-visit-bottom-buttons-main-container">
              <div className="validation-message"> {validationMessage}</div>

              <span>
                <DefaultButton
                  className="create-visit-cancel-btn"
                  toggle
                  text="Cancel"
                  onClick={() => routerHistory.goBack()}
                />
              </span>
              <span>
                <DefaultButton
                  className="create-visit-save-btn"
                  toggle
                  text="Create visit"
                  onClick={() => createVisit()}
                />
              </span>
              {
                // <span>
                // <Spinner size={SpinnerSize.xSmall}>Loading</Spinner>
                //  </span>
              }
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CreateVisit;
// <ComboBox options={comboBoxItems}></ComboBox>
